import moment from 'moment'

// Alert types configuration
export const ALERT_TYPES = [
  'DOCUMENT_RENEWAL',
  'ASSET_AUDIT_ALERT',
  'IOT_BASED_MILEAGE_ALERT',
  'IOT_BASED_DATA_GAP_ALERT',
  'IOT_BASED_FUEL_DIFF_ALERT',
  'FULL_TANK_MILEAGE_ALERT',
  'MANPOWER_ENTRY_PENDING_ALERT'
]

const ALERT_TYPE_ALIASES = {
  IOT_BASED_FUEL_DIFF: 'IOT_BASED_FUEL_DIFF_ALERT',
  IOT_BASED_FUEL_DIFFERENCE: 'IOT_BASED_FUEL_DIFF_ALERT',
  IOT_BASED_FUEL_DIFFERENCE_ALERT: 'IOT_BASED_FUEL_DIFF_ALERT',
  IOT_BASED_MILEAGE: 'IOT_BASED_MILEAGE_ALERT',
  IOT_BASED_CONSUMPTION_RATE: 'IOT_BASED_MILEAGE_ALERT',
  IOT_BASED_CONSUMPTION_RATE_ALERT: 'IOT_BASED_MILEAGE_ALERT'
}

export const normalizeAlertTypeKey = (alertType) => {
  const rawValue = String(alertType || '').trim()
  if (!rawValue) return ''
  return rawValue.toUpperCase().replace(/\s+/g, '_')
}

const canonicalizeAlertTypeKey = (alertType) => {
  const normalizedType = normalizeAlertTypeKey(alertType)
  if (!normalizedType) return ''
  return ALERT_TYPE_ALIASES[normalizedType] || normalizedType
}

export const resolveAlertTypeValue = (alertTypeName, alertType) => {
  const rawTypeName = String(alertTypeName || '').trim()
  const rawType = String(alertType || '').trim()

  if (!rawTypeName && !rawType) return ''
  if (!rawTypeName) return rawType
  if (!rawType) return rawTypeName

  const normalizedTypeName = canonicalizeAlertTypeKey(rawTypeName)
  const normalizedType = canonicalizeAlertTypeKey(rawType)

  const canonicalType = normalizedType.endsWith('_ALERT')
    ? normalizedType
    : `${normalizedType}_ALERT`

  if (
    normalizedTypeName === normalizedType
    || normalizedTypeName === canonicalType
    || `${normalizedTypeName}_ALERT` === canonicalType
  ) {
    return rawType
  }

  const formattedTypeName = formatAlertTypeLabel(rawTypeName)
  const formattedType = formatAlertTypeLabel(rawType)
  if (formattedTypeName && formattedType && formattedTypeName === formattedType) {
    return rawType
  }

  return rawTypeName
}

export const formatAlertTypeLabel = (alertType) => {
  const rawValue = String(alertType || '').trim()
  if (!rawValue) return ''
  const looksLikeEnum = rawValue.includes('_') || rawValue === rawValue.toUpperCase()
  if (!looksLikeEnum) {
    // Preserve event-based dynamic alert names as-is.
    return rawValue
  }
  const normalizedType = canonicalizeAlertTypeKey(alertType)
  if (!normalizedType) return ''
  if (normalizedType === 'IOT_BASED_FUEL_DIFF_ALERT') {
    return 'IOT BASED FUEL DIFFERENCE'
  }
  if (normalizedType === 'IOT_BASED_MILEAGE_ALERT') {
    return 'IOT BASED CONSUMPTION RATE'
  }
  return normalizedType.replace(/_ALERT$/, '').replace(/_/g, ' ').trim()
}

// Color mapping for alert types
export const getAlertTypeColor = (alertType) => {
  const colorMap = {
    'DOCUMENT_RENEWAL': { bg: '#cfe1ff', color: '#1e3a8a' },
    'ASSET_AUDIT_ALERT': { bg: '#ffd9b8', color: '#9a3412' },
    'IOT_BASED_MILEAGE_ALERT': { bg: '#d6f0cf', color: '#166534' },
    'IOT_BASED_DATA_GAP_ALERT': { bg: '#cfe9ff', color: '#0c4a6e' },
    'IOT_BASED_FUEL_DIFF_ALERT': { bg: '#ffe2b5', color: '#9a3412' },
    'IOT_BASED_DRAIN_ALERT': { bg: '#ffcfc2', color: '#9f1239' },
    'IOT_BASED_THEFT_ALERT': { bg: '#ffcfc2', color: '#9f1239' },
    'IOT_BASED_IDLING_ALERT': { bg: '#ddd6ff', color: '#4c1d95' },
    'IOT_BASED_IDILING_ALERT': { bg: '#ddd6ff', color: '#4c1d95' },
    'FULL_TANK_MILEAGE_ALERT': { bg: '#e2d1ff', color: '#5b21b6' },
    'MANPOWER_ENTRY_PENDING_ALERT': { bg: '#cfe9e8', color: '#134e4a' }
  }
  const normalizedType = canonicalizeAlertTypeKey(alertType)
  const resolvedKey = colorMap[normalizedType]
    ? normalizedType
    : (colorMap[`${normalizedType}_ALERT`] ? `${normalizedType}_ALERT` : null)

  if (resolvedKey) {
    return colorMap[resolvedKey]
  }

  // Keyword-based fallbacks for unknown/new alert types (keeps categories visually distinct)
  if (normalizedType.includes('DRAIN') || normalizedType.includes('THEFT')) {
    return { bg: '#ffcfc2', color: '#9f1239' }
  }
  if (normalizedType.includes('IDLING') || normalizedType.includes('IDILING') || normalizedType.includes('IDLE')) {
    return { bg: '#ddd6ff', color: '#4c1d95' }
  }
  if (normalizedType.includes('MILEAGE')) {
    return { bg: '#d6f0cf', color: '#166534' }
  }
  if (normalizedType.includes('FUEL') || normalizedType.includes('REFUEL')) {
    return { bg: '#ffe2b5', color: '#9a3412' }
  }
  if (normalizedType.includes('GAP') || normalizedType.includes('DATA')) {
    return { bg: '#cfe9ff', color: '#0c4a6e' }
  }

  const fallbackPalette = [
    { bg: '#cfe1ff', color: '#1e3a8a' },
    { bg: '#d6f0cf', color: '#166534' },
    { bg: '#ffe2b5', color: '#9a3412' },
    { bg: '#ddd6ff', color: '#4c1d95' },
    { bg: '#cfe9e8', color: '#134e4a' },
    { bg: '#ffcfc2', color: '#9f1239' }
  ]
  const hash = String(normalizedType || 'DEFAULT').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
  return fallbackPalette[hash % fallbackPalette.length]
}

// Status columns configuration.
// ACTION_TAKEN is a real, non-terminal status: the alert has action recorded but
// is not yet completed, so it stays on the board here until it is closed.
export const STATUS_COLUMNS = [
  { id: 'NEW', label: 'New', subtitle: 'Unread' },
  { id: 'UNDER_ANALYSIS', label: 'Under Analysis', subtitle: 'In progress' },
  { id: 'ACTION_PENDING', label: 'Action Pending', subtitle: 'Awaiting action' },
  { id: 'ACTION_TAKEN', label: 'Action Taken', subtitle: 'Action recorded' }
]

// Terminal (closed) statuses. Alerts in these states leave the board and appear
// in the closed report; the backend already excludes them from /alerts/non-closed.
export const CLOSED_STATUSES = ['CLOSED', 'IGNORED', 'DIRECT_ACTION']

// True when a status value represents a closed/terminal alert. Accepts either the
// enum form ('CLOSED') or a spaced label ('Direct Action').
export const isClosedStatus = (status) => {
  const normalized = String(status || '').toUpperCase().replace(/\s+/g, '_')
  return CLOSED_STATUSES.includes(normalized)
}

// Severity levels
export const SEVERITY = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'EMERGENCY']

// Human-friendly label for a status value, using the STATUS_COLUMNS ordering/labels
export const formatStatusLabel = (status) => {
  const found = STATUS_COLUMNS.find((col) => col.id === status)
  return found ? found.label : status
}

// Helper functions
export const severityTag = (sev) => {
  if (sev === 'CRITICAL') return { color: 'red', text: 'CRITICAL' }
  if (sev === 'HIGH') return { color: 'orange', text: 'HIGH' }
  if (sev === 'MEDIUM') return { color: 'yellow', text: 'MEDIUM' }
  if (sev === 'LOW') return { color: 'green', text: 'LOW' }
  if (sev === 'EMERGENCY') return { color: 'red', text: 'EMERGENCY' }
  return { color: 'blue', text: 'LOW' }
}

export const isOverdue = (record) => {
  if (!record.dueAt) return false
  return moment(record.dueAt).isBefore(moment(), 'day')
}

export const isDue = (record) => {
  if (!record.dueAt) return false
  return moment(record.dueAt).isSame(moment(), 'day')
}

export const getDueStatus = (record) => {
  // console.log('record', record)
  if (!record.dueAt) return null
  const dueDate = moment(record.dueAt)
  const now = moment()

  if (dueDate.isBefore(now, 'day')) {
    return { status: 'overdue', days: now.diff(dueDate, 'days') }
  } else if (dueDate.isSame(now, 'day')) {
    return { status: 'due', days: 0 }
  } else {
    const daysUntil = dueDate.diff(now, 'days')
    return { status: 'upcoming', days: daysUntil }
  }
}

export const humanAge = (ts) => {
  return moment(ts).fromNow()
}

export const formatDuration = (minutes) => {
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  } else if (minutes < 1440) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (mins === 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    }
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`
  } else {
    const days = Math.floor(minutes / 1440)
    const hours = Math.floor((minutes % 1440) / 60)
    if (hours === 0) {
      return `${days} day${days !== 1 ? 's' : ''}`
    }
    return `${days} day${days !== 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`
  }
}

// Logged-in user's id (string), or null. Used to gate actions restricted to a
// specific user (e.g. only the original assignee may complete an alert).
export const getCurrentUserId = () => {
  try {
    const userDataStr = localStorage.getItem('user')
    if (!userDataStr || userDataStr === 'undefined') {
      return null
    }
    const userData = JSON.parse(userDataStr)
    if (!userData) return null
    const id = userData._id || userData.id
    return id ? String(id) : null
  } catch (e) {
    return null
  }
}

// Resolve an alert's ORIGINAL assignee record (the FIRST assignee).
// Ordering is by original array position (`order`, stamped in
// normalizeAssigneeRecords) — i.e. assignment order, since the backend
// $push-appends. `assignTime` is deliberately NOT used: legacy records lack it, so
// a mix of a legacy original and a newly assigned (timed) user must still resolve
// to the first-assigned. `assigneeRecords` is sorted by assignTime for the current
// assignee, hence the explicit `order` field here. Returns the normalized record
// ({ userId, userName, order, ... }) or null when there are none.
const resolveOriginalAssigneeRecord = (alert) => {
  const records = Array.isArray(alert && alert.assigneeRecords) ? alert.assigneeRecords : []
  const withUser = records.filter((r) => r && r.userId)
  if (withUser.length === 0) return null
  return withUser.reduce((first, r) => {
    const rOrder = typeof r.order === 'number' ? r.order : Infinity
    const firstOrder = typeof first.order === 'number' ? first.order : Infinity
    return rOrder < firstOrder ? r : first
  }, withUser[0])
}

// Original assignee user id. Falls back to the immutable `assignedTo` owner only
// when the alert has no assignee records. Returns a string id or null.
export const getOriginalAssigneeUserId = (alert) => {
  const original = resolveOriginalAssigneeRecord(alert)
  if (original) return original.userId ? String(original.userId) : null
  const assignedTo = alert && alert.assignedTo
  return assignedTo ? String(assignedTo) : null
}

// Display name of the original assignee, or '' when it can't be resolved (e.g. an
// alert with no assignee records — only the `assignedTo` id is known, not a name).
export const getOriginalAssigneeName = (alert) => {
  const original = resolveOriginalAssigneeRecord(alert)
  return original && original.userName ? String(original.userName) : ''
}

export const getCurrentUser = () => {
  try {
    const userDataStr = localStorage.getItem('user')
    if (!userDataStr || userDataStr === 'undefined') {
      return 'Current User'
    }
    const userData = JSON.parse(userDataStr)
    return userData && (userData.email || userData.name || userData.username) ? userData.email || userData.name || userData.username : 'Current User'
  } catch (e) {
    return 'Current User'
  }
}

export const getOrganizationName = () => {
  try {
    const userDataStr = localStorage.getItem('user')
    if (!userDataStr || userDataStr === 'undefined') {
      return 'N/A'
    }
    const userData = JSON.parse(userDataStr)
    if (userData && userData.organisation) {
      return typeof userData.organisation === 'string'
        ? userData.organisation
        : (userData.organisation.name || 'N/A')
    }
    return 'N/A'
  } catch (e) {
    return 'N/A'
  }
}

export const stripHtml = (html) => {
  if (!html) return ''
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

// Extract body content from HTML - handles full HTML documents, partial HTML, or plain text
export const extractBodyContent = (html) => {
  if (!html) return ''

  // Check if it's a full HTML document (contains <html> or <body> tags)
  const isFullHtmlDocument = /<html[\s>]|<body[\s>]/i.test(html)

  if (isFullHtmlDocument) {
    // Extract content from body tag if present
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
    if (bodyMatch && bodyMatch[1]) {
      return bodyMatch[1].trim()
    }
    // If no body tag, try to extract from html tag
    const htmlMatch = html.match(/<html[^>]*>([\s\S]*?)<\/html>/i)
    if (htmlMatch && htmlMatch[1]) {
      return htmlMatch[1].trim()
    }
  }

  // Return as-is for partial HTML or plain text
  return html
}

export const getFilterValue = (alert, filterType) => {
  const fieldMap = {
    'location': 'location',
    'subLocation': 'subLocation',
    'assetModel': 'assetModel',
    'assetType': 'assetType',
    'assetSubType': 'assetSubType',
    'alertType': 'type',
    'severity': 'severity',
    'assignee': 'assignee',
    'status': 'status'
  }
  const fieldName = fieldMap[filterType]
  if (!fieldName) return ''
  const value = (alert[fieldName] || '').trim()
  if (filterType === 'assignee' && (!value || value === 'Unassigned')) {
    return 'Unassigned'
  }
  return value
}

export const getFilterTypeLabel = (filterType) => {
  const labelMap = {
    'location': 'Location',
    'subLocation': 'Sub Location',
    'assetModel': 'Asset Model',
    'assetType': 'Asset Type',
    'assetSubType': 'Asset Sub Type',
    'alertType': 'Alert Type',
    'alertDate': 'Alert Date',
    'severity': 'Severity',
    'assignee': 'Assignee',
    'status': 'Status'
  }
  return labelMap[filterType] || filterType
}
